import { externalLinks } from 'src/externalLinks';
import api from 'src/utils/ajax';

import {
  BackendSearchType,
  GroupedSearchResults,
  RawSearchResponse,
  SearchParams,
  SearchResult,
  SearchType,
} from './_types';
import { normalizeSearchResult } from './normalizers';

/**
 * Check if a search type is a backend type (requires API call)
 */
export const isBackendSearchType = (
  type: SearchType
): type is BackendSearchType => {
  return type !== 'navigation';
};

/**
 * Generalized search function that calls /sm/search API.
 * Supports all parameters from the ApiSearcher backend service.
 *
 * @param params - Search parameters
 * @returns Promise resolving to grouped search results by type
 */
export const search = async (
  params: SearchParams
): Promise<GroupedSearchResults> => {
  const {
    term,
    types,
    limit = 20,
    noLimit = false,
    trainersOnly,
    searchBy,
    locationId,
    trainerId,
    serviceId,
    packageTags,
    productTags,
    publishingChannel,
    excludePurchasedUserId,
    noUser,
    manageOnly,
  } = params;

  const response = await api.get<RawSearchResponse>('/sm/search', {
    params: {
      term,
      types,
      limit,
      no_limit: noLimit,
      trainers_only: trainersOnly,
      search_by: searchBy,
      location_id: formatIdParam(locationId),
      location_ids: formatIdArrayParam(locationId),
      trainer_id: formatIdParam(trainerId),
      trainer_ids: formatIdArrayParam(trainerId),
      service_id: formatIdParam(serviceId),
      service_ids: formatIdArrayParam(serviceId),
      package_tags: packageTags,
      product_tags: productTags,
      publishing_channel: publishingChannel,
      exclude_purchased_user_id: excludePurchasedUserId,
      no_user: noUser,
      manage_only: manageOnly,
    },
  });

  const results: GroupedSearchResults = {};

  for (const type of types) {
    const typeResults = response.data.results[type] || [];
    results[type] = typeResults.map((item) =>
      normalizeSearchResult(item, type)
    );
  }

  return results;
};

/**
 * Search and return flat array of results (useful for simple use cases)
 */
export const searchFlat = async (
  params: SearchParams
): Promise<SearchResult[]> => {
  const grouped = await search(params);
  return Object.values(grouped).flat();
};

/**
 * Helper to format single ID param (only when not an array)
 */
const formatIdParam = (
  id: SearchParams['locationId']
): string | number | undefined => {
  if (id === undefined) return undefined;
  if (Array.isArray(id)) return undefined;
  return id;
};

/**
 * Helper to format array ID param (only when is an array)
 */
const formatIdArrayParam = (
  id: SearchParams['locationId']
): (string | number)[] | undefined => {
  if (id === undefined) return undefined;
  if (Array.isArray(id)) return id;
  return undefined;
};

/**
 * Get the navigation path for a search result based on its type.
 * Returns either a relative path (for React routes) or a full URL (for external links).
 */
export const getSearchResultPath = (
  type: SearchType,
  result: SearchResult
): string | null => {
  switch (type) {
    case 'clients':
      return result.id ? `/clients/${result.id}` : null;
    case 'package':
      return result.id ? `/fbm/packages/${result.id}` : null;
    case 'plans':
      // Workout plans are edited in the v3 Ember app
      return result.id
        ? externalLinks.v3Url(`/plans/${result.id}/edit`)
        : null;
    case 'product':
      return result.id ? `/products/${result.id}` : null;
    case 'group':
      return result.id ? `/groups/${result.id}` : null;
    case 'service':
      return result.id ? `/fbm/services/${result.id}` : null;
    case 'exercise':
      return result.id ? `/exercises/${result.id}` : null;
    case 'location_trainer':
    case 'gym':
      return result.id ? `/fbm/locations/${result.id}` : null;
    case 'report':
      return result.id ? `/report/${result.id}` : null;
    default:
      return null;
  }
};

/**
 * Check if a path is an external URL (starts with http)
 */
export const isExternalUrl = (path: string): boolean => {
  return path.startsWith('http://') || path.startsWith('https://');
};

/**
 * Get a human-readable label for a search type
 */
export const getSearchTypeLabel = (type: SearchType): string => {
  const labels: Record<SearchType, string> = {
    navigation: 'Pages',
    user: 'Accounts',
    clients: 'Accounts',
    package: 'Packages',
    plans: 'Plans',
    product: 'Products',
    group: 'Groups',
    service: 'Services',
    exercise: 'Exercises',
    location_trainer: 'Locations',
    gym: 'Locations',
    coupon_purchase: 'Purchase Coupons',
    coupon_subscription: 'Subscription Coupons',
    platform: 'Platforms',
    quickbooks_account: 'QuickBooks Accounts',
    report: 'Reports',
  };

  return labels[type] || type;
};
